/* eslint-disable react/display-name */
import "./automationrecords.scss";

import { AccordionContext, useAccordionButton } from "react-bootstrap";
import { CButton, CLabel, CSwitch } from "@coreui/react";
import { Alert, Box, Tab as MuiTab, Tabs as MuiTabs } from "@mui/material";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaRegWindowClose,
  FaUserAlt,
  FaUserCheck,
} from "react-icons/fa";
import {
  downloadAutomationRecordsExport,
  useAllAutomationRecords,
  useAutomationAccuracy,
  useAutomationEfficiency,
  useAutomationRecordDetail,
  useDeleteAutomationRecordByList,
  useGetFieldsFromLatestAutomationDataRecord,
} from "../services/AutomationRecordServices";
import {
  useAutomationConfigByID,
  useAutomationConfigsByIds,
  useModelLabelClassRulesByIds,
  useModelLabelRulesByIds,
} from "../services/AutomationListServices";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useHistory, useLocation } from "react-router-dom";

import { AutomationConfigTemplateHeader } from "../CapAutoComponents";
import { sourceIdColumn } from "../shared/sourceIdColumn";
import { Cap2BetaContent } from "../beta/Cap2BetaContent";
import { useLlmScreeningEnabled } from "src/helpers/licensedRolloutGates";
import { isLlmAutomationConfig } from "../llm/llmUtils";
import {
  PredictionJustification,
  PredictionReferences,
} from "./LlmPredictionDetail";
import CIcon from "@coreui/icons-react";
import { Checkbox } from "src/reusable/components/Checkbox";
import DataTable from "react-data-table-component";
import { FormattedNumber } from "react-intl";
import { Loading } from "src/reusable/LoadingSpinner";
import { SimilarCRsSection } from "./SimilarCRs";
import { NucTooltip } from "src/reusable/components/NucTooltip";
import { PowerBIButton } from "src/reusable/components/PowerBIComponents";
import PropTypes from "prop-types";
import { RecordFilter } from "src/reusable/components/RecordFilter";
import { RecordSorter } from "src/reusable/components/RecordSorter";
import { RecordFieldsPanel } from "src/views/products/equipment-ai/components/DatasetRecordsTable";
import { debounce } from "lodash";
import { freeSet } from "@coreui/icons";
import { initialWhereFilterValue } from "src/reusable/WhereFilter";
import { nuclearn_api_url } from "src/App";
import { NucToast } from "src/reusable/components/Toast";
import { useSubsetDetailedPOCRecords } from "../../cap-trending/trendingServices";
import { LoadingButton } from "@mui/lab";
import { ButtonGroup, Menu, MenuItem } from "@mui/material";
import { ArrowDropDown, FileDownloadOutlined } from "@mui/icons-material";

const g_redacted = false;

const _userDisplayKeyMap = {
  automation_config_name: "Automation Configuration",
  confidence: "Confidence",
  label: "Predicted",
  name: "Name",
  label_type: "Label Type",
  create_date: "Create Date",
  automated: "Automated",
  manual_sample: "Manual Sample",
  source_uid: "Source Record ID",
  automation_config: "Automation Config",
  model_label_class_name: "Model Class Name",
};

let handler = {
  get: function (target, name) {
    return Object.prototype.hasOwnProperty.call(target, name)
      ? target[name]
      : name;
  },
};

let userDisplayKeyMap = new Proxy(_userDisplayKeyMap, handler);

// Three-state accuracy result. UNKNOWN means there is no ground truth for the
// field, so it cannot be scored — callers must render this as neutral, never as
// incorrect.
export const ACCURACY = {
  CORRECT: "correct",
  INCORRECT: "incorrect",
  UNKNOWN: "unknown",
};

const isBlankLabel = (value) =>
  value === null || value === undefined || String(value).trim() === "";

// Compare a prediction against its ground truth (actual) label. Returns one of
// ACCURACY.{CORRECT, INCORRECT, UNKNOWN}. When the actual label is absent the
// result is UNKNOWN — a missing ground truth is "not evaluable", not incorrect.
export const accuracyCalculation = (
  actualLabel,
  predictedLabel,
  accuracyCalculationRule,
) => {
  // No ground truth for this field -> cannot be scored.
  if (isBlankLabel(actualLabel)) return ACCURACY.UNKNOWN;

  if (
    !accuracyCalculationRule ||
    accuracyCalculationRule?.accuracy_calculation_rule === "EXACT_MATCH"
  ) {
    if (actualLabel === predictedLabel) return ACCURACY.CORRECT;
    // Ground truth exists but the prediction is missing -> incorrect.
    if (isBlankLabel(predictedLabel)) return ACCURACY.INCORRECT;
    const numA = Number(actualLabel);
    const numB = Number(predictedLabel);
    return !isNaN(numA) && !isNaN(numB) && numA === numB
      ? ACCURACY.CORRECT
      : ACCURACY.INCORRECT;
  } else if (
    accuracyCalculationRule?.accuracy_calculation_rule === "LIKE_MATCH"
  ) {
    const likeSeperator = accuracyCalculationRule?.like_seperator;
    // A blank separator would make split("") tokenize into single characters and
    // produce false matches (the flow editor can initialize it empty), so fall
    // back to whole-string equality (#29490 review).
    if (!likeSeperator) {
      return actualLabel === predictedLabel
        ? ACCURACY.CORRECT
        : ACCURACY.INCORRECT;
    }
    const predictedLabelSplit = String(predictedLabel ?? "").split(
      String(likeSeperator),
    );
    return predictedLabelSplit.includes(actualLabel)
      ? ACCURACY.CORRECT
      : ACCURACY.INCORRECT;
  } else {
    console.error(
      "This should never happen - invalid accuracy calculation rule",
    );
    return ACCURACY.UNKNOWN;
  }
};

const formatFieldName = (key) => {
  const displayName = userDisplayKeyMap[key];
  if (displayName !== key) return displayName;
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDataValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not available";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const LONG_VALUE_THRESHOLD = 480;
const LONG_VALUE_PREVIEW_LENGTH = 240;

const RecordDataValue = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const formattedValue = formatDataValue(value);
  const isLong = formattedValue.length > LONG_VALUE_THRESHOLD;
  const displayValue =
    isLong && !expanded
      ? `${formattedValue.slice(0, LONG_VALUE_PREVIEW_LENGTH)}...`
      : formattedValue;

  return (
    <>
      <span>{displayValue}</span>
      {isLong && (
        <button
          type="button"
          className="record-data-grid__expand"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
        >
          {expanded
            ? "Show less"
            : `Show full value (${formattedValue.length} characters)`}
        </button>
      )}
    </>
  );
};

const needsStackedPredictionComparison = (value) => {
  if (value && typeof value === "object") return true;
  const text = String(value ?? "");
  return text.length > 120 || text.includes("\n") || /^[{[]/.test(text.trim());
};

RecordDataValue.propTypes = {
  value: PropTypes.any,
};

export const PredictionRecord = ({
  prediction,
  datasetId,
  sourceUid,
  automationConfigId,
  modelLabelClassRule,
}) => {
  const { data: automationConfig } =
    useAutomationConfigByID(automationConfigId);
  // datasetId gates the fetch as well: a null source-uid list is the hook's
  // only disable switch, so a template with no ground-truth FK otherwise
  // POSTs to /datasets/undefined/multirecords once per prediction card.
  const { data: sourceDataRecords } = useSubsetDetailedPOCRecords(
    datasetId,
    sourceUid && datasetId != null ? [sourceUid] : null,
  );
  const sourceDataRecordData = sourceDataRecords && sourceDataRecords[0]?.data;
  const { model_output_name } = prediction;
  const predictedLabel = prediction.label;
  const actualLabel =
    sourceDataRecordData && model_output_name in sourceDataRecordData
      ? sourceDataRecordData[model_output_name]
      : null;
  const accuracyCalculationRule =
    modelLabelClassRule?.accuracy_calculation_rule;
  const accuracy = accuracyCalculation(
    actualLabel,
    predictedLabel,
    accuracyCalculationRule,
  );
  const usesStackedComparison =
    needsStackedPredictionComparison(predictedLabel) ||
    needsStackedPredictionComparison(actualLabel);
  const isLlmPrediction = isLlmAutomationConfig(automationConfig);
  return (
    <section
      className={`prediction-card prediction-card--${accuracy}`}
      aria-label={`${prediction.model_label_class_name || "Prediction"} prediction`}
    >
      <div className="prediction-card__header">
        <strong>{prediction.model_label_class_name || "Prediction"}</strong>
        <span className="prediction-card__status">
          {accuracy === ACCURACY.CORRECT
            ? "Matches ground truth"
            : accuracy === ACCURACY.INCORRECT
              ? "Does not match ground truth"
              : "Ground truth unavailable"}
        </span>
      </div>
      <dl
        className={`prediction-card__comparison ${
          usesStackedComparison ? "prediction-card__comparison--stacked" : ""
        }`}
      >
        <div>
          <dt>Predicted</dt>
          <dd>
            <FormattedLabel label={predictedLabel} />
          </dd>
        </div>
        {!isLlmPrediction && (
          <div>
            <dt>Confidence</dt>
            <dd>
              {prediction.confidence == null ? (
                "Not available"
              ) : (
                <FormattedNumber
                  style="percent"
                  value={prediction.confidence}
                  minimumFractionDigits={1}
                />
              )}
            </dd>
          </div>
        )}
        <div>
          <dt>Ground truth</dt>
          <dd>
            {sourceDataRecordData
              ? formatDataValue(actualLabel)
              : "Not available"}
          </dd>
        </div>
      </dl>
      {(prediction.justification || prediction.references?.length > 0) && (
        <div className="prediction-card__evidence">
          {prediction.justification && (
            <PredictionJustification justification={prediction.justification} />
          )}
          {prediction.references?.length > 0 && (
            <PredictionReferences references={prediction.references} />
          )}
        </div>
      )}
    </section>
  );
};

PredictionRecord.propTypes = {
  prediction: PropTypes.object.isRequired,
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sourceUid: PropTypes.string,
  automationConfigId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modelLabelClassRule: PropTypes.object,
};

const AutomationRecord = (props) => {
  // Dont run the query unless this is the active accordion row, by comparing the current active event key with the passed prop, if so reactivate the react-query
  const { activeEventKey } = useContext(AccordionContext);
  const isCurrentEventKey = activeEventKey === props.eventKey;
  const [groundTruthExpanded, setGroundTruthExpanded] = useState(false);
  const {
    data: automationDataRecord,
    isLoading: AutomationDataRecordIsLoading,
  } = useAutomationRecordDetail(props.automationRecordId, isCurrentEventKey);
  const { data: automationConfig, isLoading: automationConfigIsLoading } =
    useAutomationConfigByID(automationDataRecord?.automation_config?.id);
  const shownPredictions = (
    automationDataRecord?.automated_data_record_predictions ?? []
  )
    .filter(props.predictionFilter || (() => true))
    .map((prediction) => ({
      prediction,
      classRuleId: classRuleIdOf(
        automationConfig,
        prediction.model_label_class_name,
      ),
    }));
  const {
    data: classRulesById = NO_RULES,
    isLoading: classRulesAreLoading,
    error: classRulesError,
  } = useModelLabelClassRulesByIds(
    shownPredictions.map(({ classRuleId }) => classRuleId),
  );
  if (
    AutomationDataRecordIsLoading ||
    automationConfigIsLoading ||
    classRulesAreLoading
  ) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Loading
          height={"100px"}
          width={"100px"}
          loading_message={"Loading Record Details..."}
        />
      </div>
    );
  } else if (automationDataRecord) {
    return (
      <div className="automation-record-detail">
        <div className="automation-record-detail__title">
          Prediction record detail
        </div>
        <div className="automation-record-detail__summary">
          <div className="automation-record-detail__summary-fields">
            {Object.keys(automationDataRecord)
              .filter((x) => x in userDisplayKeyMap)
              .map((key, idx) => {
                return (
                  <div key={idx}>
                    <DetailDisplay
                      detailName={userDisplayKeyMap[key]}
                      detailData={automationDataRecord[key]}
                    />
                  </div>
                );
              })}
          </div>
        </div>
        <div className="automation-record-workspace">
          <section className="automation-record-pane automation-record-pane--predictions">
            <div className="automation-record-pane__heading">
              <h4>Predictions</h4>
              <span>{shownPredictions.length}</span>
            </div>
            {classRulesError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                data-testid="cap-auto-record-alert-rules-error"
              >
                Could not load the automation rules, so these predictions are
                compared to ground truth by exact match:{" "}
                {classRulesError.response?.data?.detail ??
                  classRulesError.message}
              </Alert>
            )}
            <div className="prediction-card-grid prediction-card-grid--pane">
              {shownPredictions.map(({ prediction, classRuleId }, idx) => {
                return (
                  <PredictionRecord
                    prediction={prediction}
                    key={idx}
                    datasetId={automationConfig?.ground_truth_dataset_fk}
                    sourceUid={automationDataRecord.source_uid}
                    automationConfigId={automationConfig?.id}
                    modelLabelClassRule={classRulesById[classRuleId]}
                  />
                );
              })}
            </div>
          </section>
          <section className="automation-record-pane automation-record-pane--input">
            <div className="automation-record-pane__heading">
              <h4>Data provided for automation</h4>
            </div>
            <AutomationRecordDataTable
              objectWithKeys={automationDataRecord.source_record_data}
              redacted={g_redacted}
            />
            <details
              className="automation-record-pane__ground-truth"
              onToggle={(event) =>
                setGroundTruthExpanded(event.currentTarget.open)
              }
            >
              <summary>Ground truth data</summary>
              {groundTruthExpanded &&
                automationConfig &&
                (automationConfig.ground_truth_dataset_fk != null ? (
                  <div className="automation-record-pane__ground-truth-content">
                    <SourceDataRecordGroup
                      datasetId={automationConfig.ground_truth_dataset_fk}
                      sourceUid={automationDataRecord.source_uid}
                      redacted={g_redacted}
                      displayAsTable
                    />
                  </div>
                ) : (
                  // Without the dataset id the group POSTs to
                  // /datasets/undefined/multirecords: the hook only disables
                  // on a null source uid.
                  <Alert severity="info">
                    No ground truth dataset is linked to this automation.
                  </Alert>
                ))}
            </details>
          </section>
        </div>
        <div className="automation-record-detail__related">
          <SimilarCRsSection
            automationRecordId={props.automationRecordId}
            groundTruthDatasetId={automationConfig?.ground_truth_dataset_fk}
            predictions={
              automationDataRecord?.automated_data_record_predictions
            }
          />
        </div>
      </div>
    );
  } else {
    return <div> No Detail</div>;
  }
};

AutomationRecord.propTypes = {
  automationRecordId: PropTypes.number.isRequired,
  eventKey: PropTypes.string.isRequired,
  predictionFilter: PropTypes.func,
};

export const MultiDetailGroup = ({ objectWithKeys, redacted }) => {
  if (!objectWithKeys || Object.keys(objectWithKeys).length === 0) {
    return <span className="detail-empty">No data available.</span>;
  }
  return (
    <dl className="record-data-grid">
      {Object.entries(objectWithKeys).map(([key, value]) => (
        <div className="record-data-grid__item" key={key}>
          <dt>{formatFieldName(key)}</dt>
          <dd className={redacted ? "redacted" : ""}>
            <RecordDataValue value={value} />
          </dd>
        </div>
      ))}
    </dl>
  );
};

const AutomationRecordDataTable = ({ objectWithKeys, redacted }) => {
  if (!objectWithKeys || Object.keys(objectWithKeys).length === 0) {
    return <span className="detail-empty">No data available.</span>;
  }
  return (
    <div
      className={`automation-record-fields-table ${redacted ? "redacted" : ""}`}
    >
      <RecordFieldsPanel data={objectWithKeys} />
    </div>
  );
};

AutomationRecordDataTable.propTypes = {
  objectWithKeys: PropTypes.object.isRequired,
  redacted: PropTypes.bool,
};

AutomationRecordDataTable.defaultProps = {
  redacted: false,
};

MultiDetailGroup.propTypes = {
  objectWithKeys: PropTypes.object.isRequired,
  redacted: PropTypes.bool.isRequired,
};
MultiDetailGroup.defaultProps = {
  redacted: false,
};

export const SourceDataRecordGroup = ({
  datasetId,
  sourceUid,
  redacted,
  emptyText,
  displayAsTable,
}) => {
  const { data: sourceDataRecords } = useSubsetDetailedPOCRecords(datasetId, [
    sourceUid,
  ]);
  return (
    <>
      {sourceDataRecords && sourceDataRecords.length > 0 ? (
        displayAsTable ? (
          <AutomationRecordDataTable
            objectWithKeys={sourceDataRecords[0]?.data}
            redacted={redacted}
          />
        ) : (
          <MultiDetailGroup
            objectWithKeys={sourceDataRecords[0]?.data}
            redacted={redacted}
          />
        )
      ) : (
        <Alert severity="info">{emptyText}</Alert>
      )}
    </>
  );
};

SourceDataRecordGroup.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sourceUid: PropTypes.string.isRequired,
  redacted: PropTypes.bool.isRequired,
  emptyText: PropTypes.string,
  displayAsTable: PropTypes.bool,
};
SourceDataRecordGroup.defaultProps = {
  redacted: false,
  emptyText: "No ground truth data uploaded for this record",
  displayAsTable: false,
};

const AccordionHeader = (props) => {
  // this needs to stay seperate, and under the accordion component, maybe some kind of weird context magic happening???
  const { activeEventKey } = useContext(AccordionContext);
  const isCurrentEventKey = activeEventKey === props.eventKey;
  return (
    <div
      style={{ display: "flex", justifyContent: "space-between" }}
      onClick={useAccordionButton(props.eventKey)}
    >
      {props.children}
      <CIcon
        size="med"
        name={isCurrentEventKey ? "cil-chevron-top" : "cil-chevron-bottom"}
      />
    </div>
  );
};

AccordionHeader.propTypes = {
  eventKey: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
};

const Checkmark = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      style={{ fill: "#00000080" }}
    >
      <path d="M 19.28125 5.28125 L 9 15.5625 L 4.71875 11.28125 L 3.28125 12.71875 L 8.28125 17.71875 L 9 18.40625 L 9.71875 17.71875 L 20.71875 6.71875 Z"></path>
    </svg>
  );
};

const BinaryIcons = ({ binaryField }) => {
  return (
    <div>
      {binaryField !== null ? (
        binaryField ? (
          <Checkmark />
        ) : (
          <div></div>
        )
      ) : (
        <CIcon size="lg" name="cil-circle" />
      )}
    </div>
  );
};

BinaryIcons.propTypes = {
  binaryField: PropTypes.bool,
};

export const AutomationEfficiency = (automationConfigTemplateId) => {
  const {
    //status: allAutomationTemplateStatus,
    data,
    isLoading,
  } = useAutomationEfficiency(automationConfigTemplateId);
  if (isLoading) {
    return (
      <Loading
        height={"30px"}
        width={"30px"}
        // loading_message={"Loading Metric"}
      />
    );
  } else {
    return data >= 0.0 ? (data * 100).toPrecision(4).toString() + "%" : "-%";
  }
};

export const AutomationAccuracy = (automationConfigTemplateId) => {
  const { data, isLoading } = useAutomationAccuracy(automationConfigTemplateId);
  if (isLoading) {
    return (
      <Loading
        height={"30px"}
        width={"30px"}
        // loading_message={"Loading Metric"}
      />
    );
  } else {
    // Distinguish "no ground truth yet" (null/undefined -> N/A) from a real 0%.
    return data != null ? (data * 100).toPrecision(4).toString() + "%" : "N/A";
  }
};

function nanToNullJSONParse(jsonString) {
  // Nans aren't valid JSON, but backend can sometimes emit them
  return JSON.parse(
    jsonString.replace(/"[^"]+"|NaN/g, function (match) {
      return match === "NaN" ? "null" : match;
    }),
  );
}

function isDoubleStringifiedJSON(str) {
  try {
    const firstParse = nanToNullJSONParse(str); // Try to parse the string once
    if (typeof firstParse !== "string") return false; // The result of the first parse should be a string
    nanToNullJSONParse(firstParse); // Try to parse the string again
    return true; // Both parses were successful, so return true
  } catch {
    return false; // Any error during parse means it's not double-stringified JSON
  }
}

export function isValidJsonArrayWithUniformKeys(jsonString) {
  let parsed;

  // Step 1: Attempt to parse the JSON string.
  try {
    parsed = nanToNullJSONParse(jsonString);
  } catch {
    return false; // The string is not valid JSON.
  }

  // Check if parsed value is an array.
  if (!Array.isArray(parsed)) return false;

  // The array should not be empty.
  if (parsed.length === 0) return false;

  // Step 2: Ensure each item in the array is an object and has the same set of keys.
  if (typeof parsed[0] !== "object" || parsed[0] === null) return false;
  const referenceKeys = Object.keys(parsed[0]).sort(); // Get keys of the first object as reference

  // Check that each object has the same keys as the reference keys.
  for (let i = 1; i < parsed.length; i++) {
    const obj = parsed[i];

    // The item should be an object.
    if (typeof obj !== "object" || obj === null) return false;

    const keys = Object.keys(obj).sort();

    // The keys should be the same as the reference keys.
    if (keys.length !== referenceKeys.length) return false;
    for (let j = 0; j < keys.length; j++) {
      if (keys[j] !== referenceKeys[j]) return false;
    }
  }

  return true;
}

const DetailTable = ({ listOfObjects }) => {
  const validKeys = Object.keys(listOfObjects[0] ?? {}).filter((key) =>
    listOfObjects.some((obj) => obj[key] !== null && obj[key] !== ""),
  );

  const renderCell = (value) => {
    if (value === null || value === undefined) return "null";

    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return value;
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", width: "100%" }}>
      <table style={{ minWidth: "500px", border: "1px solid #ddd" }}>
        {" "}
        {/* Adjust minWidth as necessary */}
        <thead>
          <tr>
            {validKeys.map((key) => (
              <th
                key={key}
                style={{
                  borderBottom: "2px solid #333",
                  padding: "8px", // Reduced padding for more compact look
                  fontWeight: "bold",
                  fontSize: "12px", // Reduced font size
                  textAlign: "left",
                  minWidth: "80px", // Adjust minWidth as necessary
                }}
              >
                {key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listOfObjects.map((row, index) => (
            <tr
              key={index}
              style={{
                backgroundColor: index % 2 === 0 ? "#f9f9f9" : "transparent",
              }}
            >
              {validKeys.map((key, i) => (
                <td
                  key={i}
                  style={{
                    padding: "8px", // Reduced padding for more compact look
                    fontSize: "12px", // Reduced font size
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  {renderCell(row[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DetailTable.propTypes = {
  listOfObjects: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export function detailSwitch(param) {
  switch (param) {
    case "Confidence":
      return (x) => {
        return (
          <FormattedNumber
            style="percent"
            value={x}
            minimumFractionDigits={2}
          />
        );
      };
    case "date":
      return (x) => {
        return new Date(Date.parse(x)).toLocaleString("en-US", {
          hour12: false,
        });
      };
    case "Manual Sample":
    case "binaryString":
      return (x) => {
        return x.toString();
      };
    case "Automation Config":
      return (x) => {
        if (typeof x === "string") {
          x = JSON.parse(x);
        }
        return x.name + ", Status: " + x.status;
      };
    case "Data Provided for Automation":
      return (x) => JSON.stringify(x);
    default:
      return (x) => {
        if (
          isDoubleStringifiedJSON(x) &&
          isValidJsonArrayWithUniformKeys(nanToNullJSONParse(x))
        ) {
          x = nanToNullJSONParse(x);
        }
        if (isValidJsonArrayWithUniformKeys(x)) {
          let parsed = nanToNullJSONParse(x);
          return <DetailTable listOfObjects={parsed} />;
        }
        return String(x);
      };
  }
}

const fRowNotManualSample = (row) => {
  return !row.NotManualSample;
};

const fRowNotAutomated = (row) => {
  return !row.automated;
};
const modelLabelClassRuleEnabled = (modelLabelClassRule) => {
  return modelLabelClassRule && modelLabelClassRule?.enabled;
};
const modelLabelRuleEnabled = (modelLabelRule) => {
  return modelLabelRule && modelLabelRule?.enabled;
};
const violatesModelLabelClassRule = (
  modelLabelClassRule,
  dataRecordPrediction,
) => {
  return (
    modelLabelClassRule?.strategy === "set_class_confidence" &&
    dataRecordPrediction?.confidence < modelLabelClassRule?.confidence
  );
};
const violatesModelLabelRule = (
  modelLabelClassRule,
  modelLabelRule,
  dataRecordPrediction,
) => {
  return (
    modelLabelClassRule.strategy &&
    modelLabelClassRule?.strategy !== "set_class_confidence" &&
    dataRecordPrediction?.confidence < modelLabelRule?.confidence
  );
};

export const DetailDisplay = (props) => {
  return (
    <div
      className={`detail-display ${
        props.flexDirection ? props.flexDirection : ""
      }`}
      {...props}
    >
      <small className="detail-name">
        <b>{props.detailName}</b>
      </small>
      <div
        className="detail-content"
        style={{ justifyContent: props.justifyContent }}
      >
        {props.children ? (
          props.children
        ) : (
          <div className={props.redacted ? "redacted" : ""}>
            {detailSwitch(props.detailName)(JSON.stringify(props.detailData))}
          </div>
        )}
      </div>
    </div>
  );
};

DetailDisplay.propTypes = {
  detailName: PropTypes.string.isRequired,
  detailData: PropTypes.string.isRequired,
  flexDirection: PropTypes.string.isRequired,
  justifyContent: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
  redacted: PropTypes.object.isRequired,
};

DetailDisplay.defaultProps = {
  flexDirection: "column",
  justifyContent: "center",
  redacted: false,
};

const NO_RULES = {};

/**
 * The automation configs of the Records page's rows, and the class and label
 * rules its cells need, each keyed by id.
 */
export const PageRulesContext = createContext({
  configsById: NO_RULES,
  classRulesById: NO_RULES,
  labelRulesById: NO_RULES,
  rulesSettled: true,
});

/** The id of a config's class rule for an output, or undefined. */
const classRuleIdOf = (automationConfig, predClassName) =>
  automationConfig?.model_label_class_rules?.[predClassName]?.id;

/** The prediction a Records cell shows for an output: the first one with that class name. */
const cellPrediction = (row, predClassName) =>
  row?.automated_data_record_predictions?.find(
    (prediction) => prediction?.model_label_class_name === predClassName,
  );

/**
 * Loads the value for `PageRulesContext`: the rows' configs, then the class
 * and label rules every cell needs, with one batched request for each kind.
 * `rulesSettled` is false while any of them loads or after one fails, and
 * `error` holds the first failure.
 */
const usePageRules = (rows) => {
  const configIds = [
    ...new Set(
      rows
        .map((row) => row.automation_config?.id)
        .filter((id) => id !== null && id !== undefined),
    ),
  ];
  const configQueries = useAutomationConfigsByIds(configIds);
  const configsById = Object.fromEntries(
    configIds.map((id, idx) => [id, configQueries[idx]?.data]),
  );
  const cells = rows.flatMap((row) =>
    [
      ...new Set(
        row.automated_data_record_predictions
          .filter((prediction) => prediction.label_type === "multiclass")
          .map((prediction) => prediction.model_label_class_name),
      ),
    ].map((predClassName) => ({
      classRuleId: classRuleIdOf(
        configsById[row.automation_config?.id],
        predClassName,
      ),
      label: cellPrediction(row, predClassName)?.label,
    })),
  );
  const classRulesQuery = useModelLabelClassRulesByIds(
    cells.map((cell) => cell.classRuleId),
  );
  const classRulesById = classRulesQuery.data ?? NO_RULES;
  const labelRulesQuery = useModelLabelRulesByIds(
    cells.map(
      (cell) =>
        classRulesById[cell.classRuleId]?.model_label_rule_ids?.[cell.label],
    ),
  );
  const queries = [...configQueries, classRulesQuery, labelRulesQuery];
  const error = queries.find((query) => query.error)?.error;
  return {
    configsById,
    classRulesById,
    labelRulesById: labelRulesQuery.data ?? NO_RULES,
    rulesSettled: !error && !queries.some((query) => query.isLoading),
    error,
  };
};

/** A Records cell: the predicted label, red when wrong, with a person icon when a rule stopped automation. */
export const PredictionDetails = ({ row, predClassName }) => {
  const { configsById, classRulesById, labelRulesById, rulesSettled } =
    useContext(PageRulesContext);
  const automationConfig = configsById[row?.automation_config?.id];
  const dataRecordPrediction = cellPrediction(row, predClassName);
  const modelLabelClassRule =
    classRulesById[classRuleIdOf(automationConfig, predClassName)];
  const modelLabelRule =
    labelRulesById[
      modelLabelClassRule?.model_label_rule_ids?.[dataRecordPrediction?.label]
    ];

  const rowNotAutomated = fRowNotAutomated(row);
  const rowNotManualSample = fRowNotManualSample(row);
  //if (row?.sou)
  const violatedModelLabelClassRule =
    modelLabelClassRuleEnabled(modelLabelClassRule) &&
    violatesModelLabelClassRule(modelLabelClassRule, dataRecordPrediction);
  const violatedModelLabelRule =
    modelLabelRuleEnabled(modelLabelRule) &&
    violatesModelLabelRule(
      modelLabelClassRule,
      modelLabelRule,
      dataRecordPrediction,
    );
  const stoppedAutomation =
    rowNotAutomated &&
    rowNotManualSample &&
    (violatedModelLabelClassRule || violatedModelLabelRule);

  return (
    <>
      {dataRecordPrediction && (
        <>
          {stoppedAutomation ? (
            // If it stoped automation, render a "manual review" person
            <FaUserAlt
              style={{
                marginRight: "5px",
                height: "10px",
                width: "10px",
              }}
            />
          ) : (
            <FaUserAlt
              style={{
                marginRight: "5px",
                paddingRight: "10px",
                height: "0px", // height and width set to 0 to generate empty spacing
                width: "0px",
              }}
            />
          )}
          <span
            style={{
              // Without settled rules a LIKE_MATCH output would be scored as an exact match.
              color:
                rulesSettled &&
                accuracyCalculation(
                  row.ground_truth_record?.data?.[
                    dataRecordPrediction.model_output_name
                  ],
                  dataRecordPrediction.label,
                  modelLabelClassRule?.accuracy_calculation_rule,
                ) === ACCURACY.INCORRECT
                  ? "red"
                  : "",
            }}
          >
            <FormattedLabel label={dataRecordPrediction.label} />
          </span>
        </>
      )}
    </>
  );
};

PredictionDetails.propTypes = {
  row: PropTypes.object,
  predClassName: PropTypes.string,
};

// Try to parse a label as JSON; returns the object if valid, null otherwise
const tryParseJsonLabel = (label) => {
  if (!label || typeof label !== "string") return null;
  try {
    const parsed = JSON.parse(label);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed;
    }
  } catch {
    // not JSON
  }
  return null;
};

// Renders a label — if JSON, reuses MultiDetailGroup; otherwise plain text
const FormattedLabel = ({ label }) => {
  const parsed = tryParseJsonLabel(label);
  if (!parsed) return <>{label || "No prediction"}</>;
  return <MultiDetailGroup objectWithKeys={parsed} redacted={g_redacted} />;
};

FormattedLabel.propTypes = {
  label: PropTypes.string,
};

const MultiOutputPredictionDetails = ({ row, predClassName }) => {
  const predictions = row?.automated_data_record_predictions?.filter(
    (prediction) =>
      prediction?.model_label_class_name === predClassName &&
      prediction.label_type !== "multiclass",
  );

  if (!predictions || predictions.length === 0) return null;

  return (
    <div
      data-testid="multi-output-prediction-details"
      style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
    >
      {predictions.map((pred, idx) => {
        const isJson = tryParseJsonLabel(pred.label) !== null;
        return isJson ? (
          <div key={idx} data-testid={`multi-output-prediction-chip-${idx}`}>
            <FormattedLabel label={pred.label} />
            {pred.confidence != null && (
              <span style={{ color: "#666", fontSize: "11px" }}>
                Confidence: {(pred.confidence * 100).toFixed(1)}%
              </span>
            )}
          </div>
        ) : (
          <span
            key={idx}
            data-testid={`multi-output-prediction-chip-${idx}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: "12px",
              backgroundColor: "#e8f4fd",
              border: "1px solid #b3d9f2",
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            {pred.label}
            {pred.confidence != null && (
              <span
                style={{ marginLeft: "4px", color: "#666", fontSize: "11px" }}
              >
                ({(pred.confidence * 100).toFixed(1)}%)
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

MultiOutputPredictionDetails.propTypes = {
  row: PropTypes.object,
  predClassName: PropTypes.string,
};

export const getColumns = (
  dataset,
  setItemsToDelete,
  itemsToDelete,
  isAdminConsole,
  selectAll,
) => {
  // Get the unique prediction fields and sort them in a list
  // Only include predictions from standard multiclass outputs (with accuracy tracking)
  let setOfPreds = new Set();
  dataset &&
    dataset.forEach((row) => {
      row.automated_data_record_predictions.forEach((prediction) => {
        if (prediction.label_type === "multiclass") {
          setOfPreds.add(prediction.model_label_class_name);
        }
      });
    });
  const listOfPreds = Array.from(setOfPreds).sort();
  // Generate some icons
  const checkMark = (
    <FaCheckCircle style={{ height: "20px", width: "20px", color: "green" }} />
  );
  const warningMark = (
    <FaExclamationTriangle
      style={{ height: "20px", width: "20px", color: "#664400" }}
    />
  );
  const unsure = (
    <FaQuestionCircle
      style={{ height: "20px", width: "20px", color: "blue" }}
    />
  );
  const userCheck = <FaUserCheck style={{ height: "20px", width: "20px" }} />;
  const xBoxBad = (
    <FaRegWindowClose style={{ height: "20px", width: "20px", color: "red" }} />
  );
  return [
    sourceIdColumn(dataset),
    {
      name: "Create Date",
      selector: (row) => detailSwitch("date")(row.create_date),
      width: "175px",
    },
    {
      name: "Automated",
      selector: (row) => {
        return (
          row.automated && (
            <NucTooltip title="This Record is Automated" id="Automated-Tooltip">
              <div>{checkMark}</div>
            </NucTooltip>
          )
        );
      },
      center: true,
      width: "65px",
      compact: true,
    },
    {
      name: (
        <span>
          Manual
          <br />
          Sample
        </span>
      ),
      selector: (row) => {
        return (
          row.manual_sample && (
            <NucTooltip title="This sample was provided manually">
              <div>{userCheck}</div>
            </NucTooltip>
          )
        );
      },
      center: true,
      width: "65px",
      compact: true,
    },
    {
      name: "Accurate",
      selector: (row) => {
        return (
          <>
            {(row.automated || row.manual_sample) &&
              (row.critical_accurate === null ? (
                <NucTooltip
                  title="Accuracy Unsure"
                  id="unsure-accuracy-tooltip"
                >
                  <div>{unsure}</div>
                </NucTooltip>
              ) : row.noncritical_accurate ? (
                <NucTooltip title="Accurate" id="accurate-tooltip">
                  <div>{checkMark}</div>
                </NucTooltip>
              ) : row.critical_accurate ? (
                <NucTooltip
                  title="Uncritical Innacurate"
                  id="uncritical-inaccurate-tooltip"
                >
                  <div>{warningMark}</div>
                </NucTooltip>
              ) : (
                xBoxBad
              ))}
          </>
        );
      },
      center: true,
      width: "65px",
      compact: true,
    },
  ]
    .concat(
      listOfPreds.map((predClassName) => {
        return {
          name: (
            <span>
              Predicted
              <br />
              {predClassName}
            </span>
          ),
          selector: (row) => (
            <PredictionDetails row={row} predClassName={predClassName} />
          ),
        };
      }),
    )
    .concat(
      isAdminConsole
        ? {
            name: "Delete",
            selector: (row) => {
              return (
                <>
                  <Checkbox
                    row={row}
                    selectAll={selectAll}
                    updateSelected={(rowToAdd) => {
                      let tmp = [...itemsToDelete];
                      // If the rowToAdd doesn't exist in itemsToDelete, add it
                      if (!tmp.find((item) => item.id === rowToAdd.id)) {
                        setItemsToDelete([...tmp, rowToAdd]);
                      } else {
                        // If it does exist and isn't selected, remove it
                        setItemsToDelete(
                          tmp.filter((item) => item.id !== rowToAdd.id),
                        );
                      }
                    }}
                  />
                </>
              );
            },
            center: true,
            width: "65px",
            compact: true,
          }
        : {},
    );
};

export const getMultiOutputColumns = (dataset) => {
  // Only include predictions from non-multiclass outputs (dynamic or multilabel)
  let setOfPreds = new Set();
  dataset &&
    dataset.forEach((row) => {
      row.automated_data_record_predictions.forEach((prediction) => {
        if (prediction.label_type !== "multiclass") {
          setOfPreds.add(prediction.model_label_class_name);
        }
      });
    });
  const listOfPreds = Array.from(setOfPreds).sort();

  return [
    sourceIdColumn(dataset),
    {
      name: "Create Date",
      selector: (row) => detailSwitch("date")(row.create_date),
      width: "175px",
    },
  ].concat(
    listOfPreds.map((predClassName) => {
      return {
        name: (
          <span>
            Predicted
            <br />
            {predClassName}
          </span>
        ),
        selector: (row) => (
          <MultiOutputPredictionDetails
            row={row}
            predClassName={predClassName}
          />
        ),
        grow: 2,
        wrap: true,
      };
    }),
  );
};

const ExpandedComponent = ({ data }) => (
  <AutomationRecord
    automationRecordId={data.id}
    predictionFilter={(p) => p.label_type === "multiclass"}
  />
);

ExpandedComponent.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

const MultiOutputExpandedComponent = ({ data }) => {
  const { data: recordDetail, isLoading } = useAutomationRecordDetail(
    data.id,
    true,
  );

  // Group predictions by model_output_name for a clean breakdown
  const predictions =
    data?.automated_data_record_predictions?.filter(
      (p) => p.label_type !== "multiclass",
    ) ?? [];
  const grouped = {};
  predictions.forEach((p) => {
    const key = p.model_label_class_name || p.model_output_name || "Unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  return (
    <div
      data-testid="multi-output-expanded"
      style={{ marginLeft: "2em", padding: "12px 0" }}
    >
      {/* Model Inputs */}
      <i>Model Inputs</i>
      {isLoading ? (
        <Loading
          height={"60px"}
          width={"60px"}
          loading_message={"Loading..."}
        />
      ) : recordDetail?.source_record_data ? (
        <div
          style={{ marginLeft: "1em", marginTop: "4px", marginBottom: "12px" }}
        >
          <MultiDetailGroup
            objectWithKeys={recordDetail.source_record_data}
            redacted={g_redacted}
          />
        </div>
      ) : (
        <div
          style={{
            color: "#999",
            fontStyle: "italic",
            marginLeft: "1em",
            marginBottom: "12px",
          }}
        >
          No input data available.
        </div>
      )}

      {/* Prediction Breakdown */}
      <i>Prediction Breakdown</i>
      {Object.entries(grouped).map(([outputName, preds]) => (
        <div
          key={outputName}
          data-testid={`multi-output-expanded-group-${outputName}`}
          style={{ margin: "8px 0" }}
        >
          <strong>{outputName}</strong>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              marginTop: "4px",
              marginLeft: "1em",
            }}
          >
            {preds
              .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
              .map((pred, idx) => {
                const conf =
                  pred.confidence != null ? pred.confidence * 100 : 0;
                return (
                  <div
                    key={idx}
                    data-testid={`multi-output-expanded-pred-${idx}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        minWidth: "150px",
                        fontSize: "13px",
                        fontWeight: pred.label ? "normal" : "italic",
                        color: pred.label ? "inherit" : "#999",
                      }}
                    >
                      <FormattedLabel label={pred.label} />
                    </div>
                    <div
                      style={{
                        flex: 1,
                        maxWidth: "200px",
                        height: "8px",
                        backgroundColor: "#e0e0e0",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${conf}%`,
                          height: "100%",
                          backgroundColor:
                            conf >= 80
                              ? "#4caf50"
                              : conf >= 50
                                ? "#ff9800"
                                : "#f44336",
                          borderRadius: "4px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        minWidth: "50px",
                      }}
                    >
                      {conf.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
      {predictions.length === 0 && (
        <div style={{ color: "#999", fontStyle: "italic", marginTop: "8px" }}>
          No predictions available for this record.
        </div>
      )}
    </div>
  );
};

MultiOutputExpandedComponent.propTypes = {
  data: PropTypes.shape({
    automated_data_record_predictions: PropTypes.array,
  }).isRequired,
};

const CAPAutomationTable = (props) => {
  const { automationConfigTemplateId } = props;
  const [page, setPage] = useState(1);
  const [numRecsPerPage, setNumRecsPerPage] = useState(50); // align with default selected option
  const [showAutomatedOnly, setshowAutomatedOnly] = useState(false);
  const [showManualOnly, setShowManualOnly] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [renderDeleteButton, setRenderDeleteButton] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [fireDelete, setFireDelete] = useState(false);
  const [isAscending, setisAscending] = useState(false);
  const [sortBy, setSortBy] = useState({
    field: { name: "create_date", column: "column_literal", group: "dataset" }, // hardcoded for now!!!
    isAscending: false,
  });

  useEffect(() => {
    if (itemsToDelete.length > 0) {
      setRenderDeleteButton(true);
    } else {
      setRenderDeleteButton(false);
    }
  }, [itemsToDelete]);
  const [whereFilters, setWhereFilters] = useState([]);

  const {
    data: automationRecords,
    isLoading: automationRecordsLoading,
    isFetching,
    isError,
    error,
  } = useAllAutomationRecords(
    automationConfigTemplateId,
    page,
    numRecsPerPage,
    whereFilters,
    props.isAdminConsole,
    sortBy,
  );

  automationRecords?.count % numRecsPerPage !== 0 ? 1 : 0;
  const handlePageChange = useCallback(
    debounce((pg) => {
      console.log("Page changed to:", pg);
      setPage(pg);
    }, 500), // Adjust the debounce delay as needed (in milliseconds)
    [],
  );
  const handlePerRowsChange = useCallback(
    debounce((newPerPage) => {
      console.log("Records per page changed to:", newPerPage);
      setNumRecsPerPage(newPerPage);
    }, 500), // Adjust the debounce delay as needed (in milliseconds)
    [],
  );

  const paginationComponentOptions = {
    selectAllRowsItem: true,
    selectAllRowsItemText: "All",
  };
  const conditionalRowStyles = [
    {
      when: (row) => row.deleted === true,
      style: {
        backgroundColor: "#FFEBEE",
      },
    },
    {
      when: (
        row, // Row was (automated or manual sample) and completely accurate!
      ) =>
        (row.automated || row.manual_sample) &&
        row.noncritical_accurate !== null &&
        row.noncritical_accurate,
      style: {
        backgroundColor: "#47c97243",
        "&:hover": {},
      },
    },
    {
      when: (
        row, // Row was (automated or manual sample) and accurate on only critical fields
      ) =>
        (row.automated || row.manual_sample) &&
        row.critical_accurate &&
        row.noncritical_accurate !== null &&
        !row.noncritical_accurate,
      style: {
        backgroundColor: "#c9c90043",
        "&:hover": {},
      },
    },
    {
      // Haven't received ground truth data yet
      when: (row) =>
        (row.automated || row.manual_sample) && row.critical_accurate === null,
      style: () => ({ backgroundColor: "#2395b743" }),
    },
    {
      // Uh oh, we got a critical inaccuracy
      when: (row) =>
        (row.automated || row.manual_sample) &&
        row.critical_accurate !== null &&
        !row.critical_accurate,
      style: {
        backgroundColor: "#c9454543",
        "&:hover": {},
      },
    },
  ];
  const multiclassRows =
    automationRecords?.data?.filter((row) =>
      row.automated_data_record_predictions.some(
        (p) => p.label_type === "multiclass",
      ),
    ) ?? [];
  const pageRules = usePageRules(multiclassRows);
  const {
    mutate: deleteAutomationRecordByList,
    status: deleteAutomationRecordByListStatus,
    error: errorDeleteAutomationRecordByList,
  } = useDeleteAutomationRecordByList(
    itemsToDelete,
    automationConfigTemplateId,
  );
  if (fireDelete) {
    deleteAutomationRecordByList(itemsToDelete, automationConfigTemplateId);
    setFireDelete(false);
  }
  useEffect(() => {
    if (deleteAutomationRecordByListStatus === "success") {
      NucToast({
        message: "Successfully soft-deleted records",
        type: "success",
      });
    }
    if (deleteAutomationRecordByListStatus === "error") {
      NucToast({
        message: errorDeleteAutomationRecordByList,
        type: "error",
      });
    }
  }, [deleteAutomationRecordByListStatus]);

  return (
    <div data-testid="cap-auto-records-table">
      <SwitchRow
        isFetching={isFetching}
        renderDeleteButton={renderDeleteButton}
        setRenderDeleteButton={setRenderDeleteButton}
        setshowAutomatedOnly={setshowAutomatedOnly}
        showAutomatedOnly={showAutomatedOnly}
        showManualOnly={showManualOnly}
        setShowManualOnly={setShowManualOnly}
        automationConfigTemplateId={automationConfigTemplateId}
        whereFilters={whereFilters}
        setWhereFilters={setWhereFilters}
        setSelectAll={setSelectAll}
        selectAll={selectAll}
        sortByCol={sortBy}
        setSortByCol={setSortBy}
        fireDelete={(isReady) => {
          if (isReady) {
            setFireDelete(true);
          }
        }}
        isAdminConsole={props.isAdminConsole}
        isAscending={isAscending}
        setisAscending={setisAscending}
      />
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="cap-auto-records-alert-error"
        >
          {error.response?.data?.detail
            ? error.response?.data?.detail
            : "Error sorting and/or filtering."}
        </Alert>
      )}
      {pageRules.error && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="cap-auto-records-alert-rules-error"
        >
          Could not load the automation rules for this page, so manual-review
          marks and accuracy colors are hidden:{" "}
          {pageRules.error.response?.data?.detail ?? pageRules.error.message}
        </Alert>
      )}
      <PageRulesContext.Provider value={pageRules}>
        <DataTable
          columns={getColumns(
            multiclassRows,
            setItemsToDelete,
            itemsToDelete,
            props.isAdminConsole,
            selectAll,
          )}
          data={multiclassRows}
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          progressPending={automationRecordsLoading || isFetching}
          progressComponent={
            <div data-testid="cap-auto-records-loading">
              <Loading
                height={"250px"}
                width={"250px"}
                loading_message={"Loading Automation Records..."}
              />
            </div>
          }
          pagination
          paginationComponentOptions={paginationComponentOptions}
          paginationServer
          paginationTotalRows={automationRecords?.count}
          paginationPerPage={numRecsPerPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          dense
          highlightOnHover
          pointerOnHover
          conditionalRowStyles={conditionalRowStyles}
          expandOnRowDoubleClicked
        />
      </PageRulesContext.Provider>
    </div>
  );
};

CAPAutomationTable.propTypes = {
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  isAdminConsole: PropTypes.bool,
};

const MultiOutputTable = ({ automationConfigTemplateId }) => {
  const [page, setPage] = useState(1);
  const [numRecsPerPage, setNumRecsPerPage] = useState(50);
  const [sortBy] = useState({
    field: { name: "create_date", column: "column_literal", group: "dataset" },
    isAscending: false,
  });

  const {
    data: automationRecords,
    isLoading,
    isFetching,
    isError,
    error,
  } = useAllAutomationRecords(
    automationConfigTemplateId,
    page,
    numRecsPerPage,
    [],
    false,
    sortBy,
  );

  const handlePageChange = useCallback(
    debounce((pg) => setPage(pg), 500),
    [],
  );
  const handlePerRowsChange = useCallback(
    debounce((newPerPage) => setNumRecsPerPage(newPerPage), 500),
    [],
  );

  // Filter to only records that have non-multiclass predictions (dynamic or multilabel)
  const filteredData = automationRecords?.data?.filter((row) =>
    row.automated_data_record_predictions.some(
      (p) => p.label_type !== "multiclass",
    ),
  );

  return (
    <div data-testid="cap-auto-multi-output-table">
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 1,
        }}
        data-testid="cap-auto-multi-output-toolbar"
      >
        <Box sx={{ mr: 1.25 }} data-testid="cap-auto-multi-output-btn-powerbi">
          <PowerBIButton
            url={`${nuclearn_api_url}/products/cap-screening-automation/automation-records?automation_config_template_id=${automationConfigTemplateId}&prediction_filter=non_multiclass&expand_predictions=true`}
            forPage="automationRecords"
          />
        </Box>
        <DownloadAuditTableCsvButton
          autoConfigId={automationConfigTemplateId}
          whereFilters={[]}
          sortBy={sortBy}
          predictionFilter="non_multiclass"
        />
      </Box>
      <Alert
        severity="info"
        sx={{ fontSize: "13px", mb: 1.5 }}
        data-testid="cap-auto-multi-output-alert"
      >
        This tab displays predictions from <strong>Multi Label</strong> model
        outputs. Results are display-only and are not included in accuracy or
        KPI calculations.
      </Alert>
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="cap-auto-multi-output-alert-error"
        >
          {error.response?.data?.detail || "Error loading records."}
        </Alert>
      )}
      {!isLoading &&
      !isFetching &&
      (!filteredData || filteredData.length === 0) ? (
        <Alert
          severity="warning"
          data-testid="cap-auto-multi-output-alert-empty"
        >
          No multi-output results found. To see results here, configure a model
          output as Multi Label in the Autoflow configuration.
        </Alert>
      ) : (
        <DataTable
          columns={getMultiOutputColumns(filteredData)}
          data={filteredData}
          expandableRows
          expandableRowsComponent={MultiOutputExpandedComponent}
          progressPending={isLoading || isFetching}
          progressComponent={
            <Loading
              height={"250px"}
              width={"250px"}
              loading_message={"Loading Multi-Output Records..."}
            />
          }
          pagination
          paginationComponentOptions={{
            selectAllRowsItem: true,
            selectAllRowsItemText: "All",
          }}
          paginationServer
          paginationTotalRows={automationRecords?.count}
          paginationPerPage={numRecsPerPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          dense
          highlightOnHover
          pointerOnHover
          expandOnRowDoubleClicked
        />
      )}
    </div>
  );
};

MultiOutputTable.propTypes = {
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

const DownloadAuditTableCsvButton = ({
  autoConfigId,
  whereFilters,
  sortBy,
  predictionFilter,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const startDownload = (format) => {
    if (isDownloading) return;
    setIsDownloading(true);
    setMenuAnchor(null);
    downloadAutomationRecordsExport(
      autoConfigId,
      () => setIsDownloading(false),
      whereFilters,
      sortBy,
      format,
      predictionFilter,
    );
  };

  const menuId = "cap-auto-records-download-menu";
  const menuTriggerId = "cap-auto-records-download-menu-trigger";

  return (
    <>
      <ButtonGroup
        variant="contained"
        color="success"
        disableElevation
        disabled={isDownloading}
      >
        <LoadingButton
          loading={isDownloading}
          loadingPosition="start"
          variant="contained"
          disableElevation
          color="success"
          data-testid="cap-auto-records-btn-download"
          data-attr="cap-ai:btn:download-automation-records-csv"
          startIcon={<FileDownloadOutlined />}
          onClick={() => startDownload("csv")}
        >
          {isDownloading ? "Downloading" : "Download CSV"}
        </LoadingButton>
        <LoadingButton
          id={menuTriggerId}
          variant="contained"
          color="success"
          disableElevation
          size="small"
          data-testid="cap-auto-records-btn-download-menu"
          aria-label="select download format"
          aria-haspopup="menu"
          aria-controls={menuOpen ? menuId : undefined}
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={isDownloading}
        >
          <ArrowDropDown />
        </LoadingButton>
      </ButtonGroup>
      <Menu
        id={menuId}
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ "aria-labelledby": menuTriggerId }}
      >
        <MenuItem
          onClick={() => startDownload("csv")}
          data-testid="cap-auto-records-btn-download-csv"
          data-attr="cap-ai:btn:download-automation-records-csv"
        >
          Download as CSV
        </MenuItem>
        <MenuItem
          onClick={() => startDownload("xlsx")}
          data-testid="cap-auto-records-btn-download-xlsx"
          data-attr="cap-ai:btn:download-automation-records-xlsx"
        >
          Download as XLSX
        </MenuItem>
      </Menu>
    </>
  );
};

DownloadAuditTableCsvButton.propTypes = {
  autoConfigId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  whereFilters: PropTypes.array.isRequired,
  sortBy: PropTypes.object,
  predictionFilter: PropTypes.oneOf(["all", "multiclass", "non_multiclass"]),
};

DownloadAuditTableCsvButton.defaultProps = {
  predictionFilter: "all",
};

const SwitchRow = ({
  isFetching,
  renderDeleteButton,
  setRenderDeleteButton,
  setshowAutomatedOnly,
  showAutomatedOnly,
  showManualOnly,
  setShowManualOnly,
  automationConfigTemplateId,
  whereFilters,
  setWhereFilters,
  isAscending,
  setisAscending,
  sortByCol,
  setSortByCol,
  fireDelete,
  isAdminConsole,
}) => {
  // fields are returned from this and used in the RecrodFilter component for SortBy input options
  const { data: fields } = useGetFieldsFromLatestAutomationDataRecord(
    automationConfigTemplateId,
  );

  const [tempWhereFilters, setTempWhereFilters] = useState([
    initialWhereFilterValue,
  ]);
  const [advancedOptionsEnabled, setAdvancedOptionsEnabled] = useState(false);

  useEffect(() => {
    if (!advancedOptionsEnabled) {
      setWhereFilters([]);
    }
  }, [advancedOptionsEnabled]);

  const resetFilters = () => {
    setWhereFilters([]);
    setTempWhereFilters([initialWhereFilterValue]);
  };

  return (
    <div
      style={{
        display: "flex",
        //alignItems: "center",
        marginBottom: "16px",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          maxWidth: "220px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginBottom: "23px",
          }}
        >
          <div
            style={{ marginRight: "15px" }}
            data-testid="cap-auto-records-filter-automated"
          >
            <CLabel style={{ marginBottom: "5px" }}>Automated</CLabel>
            <span style={{ display: "flex", alignItems: "center" }}>
              <CSwitch
                data-testid="cap-auto-records-chk-automated-all"
                data-attr="cap-ai:toggle:filter-automated-only"
                onChange={() => {
                  setshowAutomatedOnly(!showAutomatedOnly);
                  if (!showAutomatedOnly) {
                    const newFilterOn = {
                      group: "automation_records",
                      name: "automated",
                      column: "column_literal",
                    };
                    const newWhereFilter = {
                      filter_on: newFilterOn,
                      comparison_operator: "eq",
                      filter_value: "true",
                      value_datatype: "boolean",
                    };
                    setWhereFilters([newWhereFilter]);
                  } else {
                    setWhereFilters([]);
                  }
                  setRenderDeleteButton(false);
                }}
                disabled={showManualOnly || advancedOptionsEnabled}
                checked={showAutomatedOnly}
                size="lg"
                labelOff="All"
                color="success"
              ></CSwitch>
            </span>
          </div>

          <div
            style={{ marginRight: "15px" }}
            data-testid="cap-auto-records-filter-manual"
          >
            <CLabel style={{ marginBottom: "5px" }}>Manual</CLabel>
            <span style={{ display: "flex", alignItems: "center" }}>
              <CSwitch
                data-testid="cap-auto-records-chk-manual-all"
                data-attr="cap-ai:toggle:filter-manual-only"
                size="lg"
                color="success"
                labelOff="All"
                onChange={() => {
                  setShowManualOnly(!showManualOnly);
                  if (!showManualOnly) {
                    const newFilterOn = {
                      group: "automation_records",
                      name: "manual_sample",
                      column: "column_literal",
                    };
                    const newWhereFilter = {
                      filter_on: newFilterOn,
                      comparison_operator: "eq",
                      filter_value: "true",
                      value_datatype: "boolean",
                    };
                    setWhereFilters([newWhereFilter]);
                  } else {
                    setWhereFilters([]);
                  }
                  setRenderDeleteButton(false);
                }}
                checked={showManualOnly}
                disabled={showAutomatedOnly || advancedOptionsEnabled}
              ></CSwitch>
            </span>
          </div>
          <div>
            <CLabel style={{ marginBottom: "5px" }}>Advanced</CLabel>
            <span
              style={{
                marginLeft: "15px",
                marginRight: "25px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CSwitch
                data-testid="cap-auto-records-chk-advanced"
                data-attr="cap-ai:toggle:advanced-filters"
                size="lg"
                color="info"
                disabled={showAutomatedOnly || showManualOnly}
                onChange={() => {
                  setAdvancedOptionsEnabled(!advancedOptionsEnabled);
                }}
                checked={advancedOptionsEnabled}
              ></CSwitch>
            </span>
          </div>
        </div>
        {advancedOptionsEnabled && (
          <RecordSorter
            fields={fields}
            isAscending={isAscending}
            setisAscending={setisAscending}
            sortByCol={sortByCol}
            setSortByCol={setSortByCol}
            testIdPrefix="cap-auto-records"
          />
        )}
      </div>
      {advancedOptionsEnabled && (
        <div style={{ marginLeft: "15px" }}>
          <RecordFilter
            whereFilters={whereFilters}
            setWhereFilters={setWhereFilters}
            id={automationConfigTemplateId}
            group="automation_records"
            fields={fields}
            tempWhereFilters={tempWhereFilters}
            setTempWhereFilters={setTempWhereFilters}
            isFetching={isFetching}
            resetFilters={resetFilters}
            testIdPrefix="cap-auto-records"
          />
        </div>
      )}
      <div
        style={{ marginLeft: "15px", marginRight: "15px", maxWidth: "500px" }}
        data-testid="cap-auto-records-sort-by"
      >
        {!advancedOptionsEnabled && (
          <RecordSorter
            fields={fields}
            isAscending={isAscending}
            setisAscending={setisAscending}
            sortByCol={sortByCol}
            setSortByCol={setSortByCol}
            testIdPrefix="cap-auto-records"
          />
        )}
      </div>

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
        }}
      >
        {renderDeleteButton ? (
          <>
            <CButton
              style={{ marginRight: "10px" }}
              color="danger"
              value={"Delete"}
              data-attr="cap-ai:btn:delete-automation-records"
              onClick={() => {
                fireDelete(true);
              }}
            >
              <CIcon content={freeSet.cilTrash} />
            </CButton>
          </>
        ) : (
          <></>
        )}

        <div
          style={
            isAdminConsole
              ? {
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-around",
                  alignItems: "center",
                }
              : {
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }
          }
        >
          <div
            style={
              isAdminConsole ? { marginTop: "10px" } : { marginRight: "10px" }
            }
            data-testid="cap-auto-records-btn-powerbi"
          >
            <PowerBIButton
              url={`${nuclearn_api_url}/products/cap-screening-automation/automation-records?automation_config_template_id=${automationConfigTemplateId}&prediction_filter=multiclass&expand_predictions=true`}
              forPage="automationRecords"
            />
          </div>
          <DownloadAuditTableCsvButton
            autoConfigId={automationConfigTemplateId}
            whereFilters={whereFilters}
            sortBy={sortByCol}
            predictionFilter="multiclass"
          />
        </div>
      </div>
    </div>
  );
};

SwitchRow.propTypes = {
  whereFilters: PropTypes.array.isRequired,
  setWhereFilters: PropTypes.func.isRequired,
  renderDeleteButton: PropTypes.bool.isRequired,
  setRenderDeleteButton: PropTypes.func.isRequired,
  setshowAutomatedOnly: PropTypes.func.isRequired,
  showAutomatedOnly: PropTypes.bool.isRequired,
  showManualOnly: PropTypes.bool.isRequired,
  setShowManualOnly: PropTypes.func.isRequired,
  selectAll: PropTypes.bool.isRequired,
  setSelectAll: PropTypes.func.isRequired,
  itemsToDelete: PropTypes.array,
  fireDelete: PropTypes.func,
  isAscending: PropTypes.bool.isRequired,
  setisAscending: PropTypes.func.isRequired,
  setSortByCol: PropTypes.func.isRequired,
  sortByCol: PropTypes.object.isRequired,
  isAdminConsole: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  automationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
};

export const CAPAutomationRecords = (props) => {
  const automationConfigTemplateId =
    props?.match?.params?.automation_config_template_id;
  const resolvedId = props.automationConfigTemplateId
    ? props.automationConfigTemplateId
    : automationConfigTemplateId;
  const history = useHistory();
  const location = useLocation();
  // CAP 2.0 is a sibling view of the records table, so it is a sub-tab rather
  // than a switch above the tab row (#2521). The PostHog rollout gate from
  // #2547 still applies: with cap-llm-screening off, ?view=v2 must not open
  // the beta view and the sub-tab must not render.
  const llmScreeningEnabled = useLlmScreeningEnabled();
  const isV2 =
    llmScreeningEnabled &&
    new URLSearchParams(location.search).get("view") === "v2";
  const [subTab, setSubTab] = useState(isV2 ? "cap2" : "records");
  // isV2 is not stable at mount: the PostHog flag resolves after first render
  // and back/forward changes location.search without remounting. Only the
  // cap2 sub-tab is derived from ?view=v2, so leaving it must not clobber a
  // sub-tab the user just picked (e.g. multi-output, which also drops the param).
  useEffect(() => {
    setSubTab((current) =>
      isV2 ? "cap2" : current === "cap2" ? "records" : current,
    );
  }, [isV2]);
  const selectSubTab = (next) => {
    setSubTab(next);
    const params = new URLSearchParams(location.search);
    if (next === "cap2") {
      params.set("view", "v2");
    } else {
      params.delete("view");
    }
    // "?" prefix matters: some history implementations treat a bare string
    // as the full search value (usePageUrlSync prefixes it the same way).
    history.replace({ search: `?${params.toString()}` });
  };
  return (
    <div style={{ padding: "24px" }} data-testid="cap-auto-records-container">
      <AutomationConfigTemplateHeader
        title="Automation Records"
        automationConfigTemplateId={resolvedId}
        selectedButton="audit"
        testIdPrefix="cap-auto-records"
      />
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <MuiTabs
          value={subTab}
          onChange={(_, v) => selectSubTab(v)}
          data-testid="cap-auto-records-tabs"
        >
          <MuiTab
            value="records"
            label="Records"
            data-testid="cap-auto-records-tab-records"
            data-attr="cap-ai:tab:automation-records"
            sx={{ textTransform: "none", minHeight: 48 }}
          />
          {llmScreeningEnabled ? (
            <MuiTab
              value="cap2"
              label={
                <span>
                  CAP 2.0{" "}
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "normal",
                      opacity: 0.7,
                    }}
                  >
                    (Beta)
                  </span>
                </span>
              }
              data-testid="cap2-beta-entry"
              data-attr="cap-ai:tab:cap2-beta"
              sx={{ textTransform: "none", minHeight: 48 }}
            />
          ) : null}
          <MuiTab
            value="multi-output"
            label={
              <span>
                Multi-Output Results{" "}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "normal",
                    opacity: 0.7,
                  }}
                >
                  (Beta)
                </span>
              </span>
            }
            data-testid="cap-auto-records-tab-multi-output"
            data-attr="cap-ai:tab:multi-output-results"
            sx={{ textTransform: "none", minHeight: 48 }}
          />
        </MuiTabs>
      </Box>
      {subTab === "records" && (
        <CAPAutomationTable
          isAdminConsole={props.isAdminConsole}
          automationConfigTemplateId={resolvedId}
        />
      )}
      {subTab === "cap2" && <Cap2BetaContent templateId={resolvedId} />}
      {subTab === "multi-output" && (
        <MultiOutputTable automationConfigTemplateId={resolvedId} />
      )}
    </div>
  );
};

CAPAutomationRecords.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      automation_config_template_id: PropTypes.string,
    }),
  }),
  automationConfigTemplateId: PropTypes.number,
  isAdminConsole: PropTypes.bool,
};
